import { Grid, GridProps, Group, Paper, Stack, Switch, Text } from '@mantine/core';

import { MultilingualCodeSystems } from '@e-origin/shared';

import { DateTimePickerInput, EucdmCode, List, MultiLangCodes, NumberInput, TextInput } from '@components';
import { createPolicyArrayField, createPolicyField, hasVisibleChild, PolicyForm, WidgetContext } from '../createPolicyField';

/**
 * The default renderers for a policy field.
 *
 * A SEPARATE ENTRY POINT, not part of `@field-policy`, and that is load-bearing rather than tidiness:
 * this module imports `@components`, whose barrel re-exports `HideElementFor` -> `useSettings` ->
 * `window.location` at module load. The `@field-policy` barrel is in the module graph of every pure
 * policy test suite, so pulling this into it would make all of them unloadable in node.
 *
 * `createPolicyField` stays exported from the main barrel: a feature whose widgets, wrapper or
 * common props differ builds its own instead of using these. TransitView, for instance, wraps
 * nothing in <EucdmCode>, where DeclarationView does it 111 times.
 */

/**
 * The props a call site adds on top of the framework's base ones.
 *
 * These are the escape hatches the design budgeted for, plus the layout inputs. Needing a further
 * one is a signal that the field belongs in hand-written JSX instead.
 */
export interface PolicyFieldExtra {
  span: number;
  /** For `widget: 'readonly'` fields, whose value comes from the entity rather than the form. */
  value?: string | number;
  /** EUCDM hint path, when it differs from the form path. */
  eucdmPath?: string;
  /**
   * Set false for fields NOT wrapped in <EucdmCode>. Wrapping adds a Box to the DOM, so this is a
   * layout decision rather than a cosmetic one.
   */
  eucdm?: boolean;
  /** Restricts the codelist options, e.g. the procedures allowed for the current group. */
  includeList?: string[];
}

type Ctx = WidgetContext<PolicyFieldExtra>;

const systemsOf = (rule: Ctx['rule']) => rule.systems as MultilingualCodeSystems[];

const codes = ({ inputProps, common, rule, rightSection, extra, form, path }: Ctx) => (
  <MultiLangCodes
    {...inputProps}
    {...common}
    code={rule.code}
    system={systemsOf(rule)}
    rightSection={rightSection}
    includeList={extra.includeList}
    {...(rule.coerce === 'number'
      ? {
          value: inputProps.value?.toString(),
          onChange: (selected: string) => form?.setFieldValue(path, Number(selected)),
        }
      : {})}
  />
);

/**
 * Shared by `PolicyField` and `PolicyArrayField`, so an item sub-field inside an array renders
 * through the exact same widget as a top-level field of the same `widget` name - a `text`/`codes`
 * sub-field looks identical either way, with no second implementation to keep in step.
 */
const widgets = {
  readonly: ({ common, extra }: Ctx) => <TextInput {...common} value={extra.value ?? ''} disabled />,
  number: ({ inputProps, common, rightSection }: Ctx) => <NumberInput {...inputProps} {...common} rightSection={rightSection} />,
  'codes-multi': ({ inputProps, common, rule }: Ctx) => (
    <MultiLangCodes {...inputProps} {...common} multiple searchable clearable hidePickedOptions code={rule.code} system={systemsOf(rule)} />
  ),
  codes,
  text: ({ inputProps, common, rightSection }: Ctx) => <TextInput {...inputProps} {...common} rightSection={rightSection} />,
  datetime: ({ inputProps, common }: Ctx) => (
    <DateTimePickerInput
      {...inputProps}
      {...common}
      value={inputProps.value ? new Date(inputProps.value) : null}
      onChange={(value: Date | null) => inputProps.onChange?.(value ? value.toISOString() : null)}
    />
  ),
};

const commonProps = ({ rule, disabled }: { rule: Ctx['rule']; disabled: boolean }) => ({
  label: rule.label,
  size: 'lg' as const,
  disabled,
  'data-pw': rule.dataPw,
});

/** Shared by `PolicyField` and `PolicyArrayField` - a field's header toggle looks the same either way. */
const renderHeaderToggle = (form: PolicyForm, togglePath: string, toggleValue: unknown, { disabled, dataPw }: { disabled: boolean; dataPw?: string }) => (
  <Switch {...form.getInputProps(togglePath)} checked={Boolean(toggleValue)} size="xs" disabled={disabled} data-pw={dataPw && `${dataPw}-toggle`} />
);

/**
 * Renders one policy-resolved field, or nothing when the policy says it is absent.
 *
 * Replaces a `<HideElementFor>` wrapper plus an inline amendment-lock call at each site. The JSX
 * around it stays hand-written - this does not generate layout.
 */
export const PolicyField = createPolicyField<PolicyFieldExtra>({
  fallbackWidget: 'text',
  commonProps,
  renderHeaderToggle,
  widgets,
  layout: (input, { path, extra }) => (
    <Grid.Col span={extra.span}>{extra.eucdm === false ? input : <EucdmCode path={extra.eucdmPath ?? path}>{input}</EucdmCode>}</Grid.Col>
  ),
});

/**
 * Renders an array-of-objects field: one `<List>` (add/remove, whole-array replace), one call per
 * registered item sub-field per index - see `createPolicyArrayField`'s own comment for why an
 * item's fields need no per-item `resolve()` of their own.
 */
export const PolicyArrayField = createPolicyArrayField<PolicyFieldExtra>({
  fallbackWidget: 'text',
  commonProps,
  renderHeaderToggle,
  widgets,
  renderList: ({ label, items, onChange, disabled, dataPw, addButtonLabel, createNewItem, headerToggle, renderItem }) => {
    const hasHeader = Boolean(label) || Boolean(headerToggle);
    const showFrame = hasHeader || items.length > 0;

    return (
      <Paper shadow={showFrame ? 'xs' : undefined} p="sm" withBorder={showFrame} style={showFrame ? undefined : { boxShadow: 'none' }}>
        <Stack gap="sm">
          {hasHeader && (
            <Group justify="space-between">
              <Text fw={500} size="lg">
                {label}
              </Text>
              {headerToggle}
            </Group>
          )}
          <List mt={0} items={items} onChange={onChange} disabled={disabled} createNewItem={createNewItem} addButtonLabel={addButtonLabel} data-pw={dataPw}>
            {(item, index, helpers) => (
              <List.Element
                p="sm"
                variant={items.length > 0 || hasHeader ? 'paper' : undefined}
                onRemove={helpers.remove}
                canRemove={helpers.canRemove}
                disabled={disabled}
              >
                <Stack gap="sm">{renderItem(item, index)}</Stack>
              </List.Element>
            )}
          </List>
        </Stack>
      </Paper>
    );
  },
  layout: (input, { path, extra }) => (
    <Grid.Col span={extra.span}>{extra.eucdm === false ? input : <EucdmCode path={extra.eucdmPath ?? path}>{input}</EucdmCode>}</Grid.Col>
  ),
});

/**
 * A grid row that disappears when none of its fields would render.
 *
 * Replaces the `{anyPresent(A, B) && <Grid>...}` pattern, which restated the path list next to the
 * fields and could drift from it silently. Emptiness is derived from the children, so it cannot
 * disagree with what is actually inside.
 *
 * Deliberately defaults nothing: it is a <Grid> that can vanish, so the margin stays written at the
 * call site and one row differing from its neighbours stays visible in the JSX.
 */
export const FieldRow: React.FC<GridProps> = ({ children, ...gridProps }) => (hasVisibleChild(children) ? <Grid {...gridProps}>{children}</Grid> : null);
